import { Bus } from '../core/bus';
import type { PoseFrame } from '../pose/types';
import { ComboAssembler } from './combo';
import { DefenseRecogniser } from './defense';
import type { GuardState, RecogniserEvents } from './events';
import { FootworkRecogniser } from './footwork';
import { PunchRecogniser } from './punch';

export * from './events';
export { ComboAssembler, NAMED_COMBOS, nameCombo } from './combo';
export { PUNCH_TUNING } from './punch';
export { DEFENSE_TUNING } from './defense';
export { FOOTWORK_TUNING } from './footwork';

/**
 * The single entry point from vision into gameplay.
 *
 * Everything downstream subscribes to this bus. That indirection is what lets a
 * replay recorder, a drill grader and the AI all consume the identical stream —
 * and it is what makes replay-from-log possible without a camera attached.
 */
export class Recogniser {
  readonly bus = new Bus<RecogniserEvents>();
  private punches = new PunchRecogniser();
  private defense = new DefenseRecogniser();
  private footwork = new FootworkRecogniser();
  private combos = new ComboAssembler();

  get guard(): GuardState {
    return this.defense.state;
  }

  get handsBusy(): number {
    return this.punches.busyHands;
  }

  reset(): void {
    this.punches.reset();
    this.defense.reset();
    this.footwork.reset();
    this.combos.reset();
  }

  update(f: PoseFrame): void {
    const p = this.punches.update(f);
    for (const side of p.onsets) this.bus.emit('punch-start', { side, t: f.t });
    for (const punch of p.peaks) {
      const closed = this.combos.push(punch);
      if (closed) this.bus.emit('combo', closed);
      this.bus.emit('punch', punch);
    }
    // `recovered` re-fires the same object with recoveryMs filled in. Consumers
    // that care about hand-return discipline (scoring) read it on the combo,
    // by which time every punch in the run has been patched.

    const d = this.defense.update(f);
    if (d) this.bus.emit('defense', d);

    const fw = this.footwork.update(f);
    if (fw) this.bus.emit('footwork', fw);

    const timedOut = this.combos.tick(f.t);
    if (timedOut) this.bus.emit('combo', timedOut);
  }
}
